import { useEffect, useRef, useState } from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  ZENDESK_LAUNCHER_IFRAME_ID,
  ZENDESK_OPEN_EVENT,
  ZENDESK_WIDGET_IFRAME_ID,
  type ZendeskLoadState,
  type ZendeskWidgetLoader,
  createZendeskWidgetLoader,
  hideWidget,
  showWidget,
  watchForWidgetPaint,
} from "./zendesk-widget-loader";

/**
 * Click-to-load facade for the Zendesk Web Widget (Classic) — COM-183.
 *
 * Replaces `ZendeskSnippetScript` (SFCC-11018 / ANALYTICS-30), which injected
 * the zdassets snippet on every page after `load` + a 3s-capped idle callback
 * — i.e. unconditionally, interaction or not, putting the ~463KB widget bundle
 * on every page view's critical path (Lighthouse's zero-interaction pass
 * included). This component instead SSRs a lightweight launcher lookalike and
 * loads zero widget bytes until either:
 *
 *  (a) the facade (or the footer "Live Chat" trigger, via
 *      {@link ZENDESK_OPEN_EVENT}) is clicked — the snippet loads with a
 *      loading state on the facade, and the widget is opened programmatically
 *      once booted, preserving the click intent; or
 *  (b) an idle slot after the FIRST user interaction (pointerdown / keydown /
 *      wheel / touchstart — see INTERACTION_EVENTS for why not `scroll`) — a
 *      background load with no open, so the
 *      real launcher takes over seamlessly and any Zendesk-console-side
 *      behavior (pre-chat form, department routing, proactive-chat triggers)
 *      resumes exactly as configured. The facade defers the widget; it doesn't
 *      delete any of its console-configured behavior.
 *
 * Visual parity (all values verified against the live launcher on
 * fsa-develop): desktop is a bottom-right 46px pill (10px/20px offsets,
 * 22px x-padding, no shadow) with a 20px white chat glyph + 15px/700 "Chat"
 * label; mobile (the widget UA-sniffs — approximated here with the `sm`
 * breakpoint) is a 48px icon-only circle. Brand color comes from the Zendesk
 * account config (ekr compose `launcher.props.color`: FSA `#ff295b`, HSA
 * `#785aff`), surfaced as `--zendesk-launcher-bg` in `app/styles/app.css` per
 * site class.
 *
 * Handoff: `ready` (widget booted) is NOT the unmount signal — measured live,
 * the launcher iframe finishes revealing ~550ms after the boot callback. The
 * facade stays mounted (rendering identical pixels above the fading real
 * launcher) until {@link watchForWidgetPaint} sees a visible launcher/widget
 * iframe, so the corner is never empty and never visibly double-rendered.
 *
 * Gating is unchanged: `src` is the root-loader-resolved snippet URL
 * (`getZendeskSnippetSrc` — null on TPA/ISE, `zendeskChatEnabled` false, no
 * snippet id, or Builder preview), so WDH and suppressed contexts render
 * nothing. Chat load is not consent-gated today (it never ran through the GTM
 * consent cascade) and that is intentionally unchanged.
 *
 * SSR-safe: the facade button itself SSRs (visible before hydration — earlier
 * than the old real launcher ever appeared); all loader logic runs client-only
 * inside effects/handlers. A click in the pre-hydration window is dropped,
 * which is strictly better than the status quo where no launcher existed at
 * all until well after load.
 */

const messages = defineMessages({
  chatLabel: {
    id: "zendeskChat.facade.chat_label",
    defaultMessage: "Chat",
  },
  openChat: {
    id: "zendeskChat.facade.open_chat",
    defaultMessage: "Open live chat",
  },
  loadingChat: {
    id: "zendeskChat.facade.loading_chat",
    defaultMessage: "Loading live chat",
  },
});

/**
 * First-interaction signals that arm the background idle load. Deliberately
 * NO `scroll`: the browser's scroll restoration on reload/back-forward and
 * RR7's `<ScrollRestoration>` both fire trusted `scroll` events with zero user
 * input, which would load the widget on interactionless page views. Real
 * user scrolling is covered by `wheel` / `touchstart` / `keydown`.
 */
const INTERACTION_EVENTS = [
  "pointerdown",
  "keydown",
  "wheel",
  "touchstart",
] as const;

export function ZendeskChatFacade({
  src,
  nonce,
}: {
  src: string | null;
  nonce?: string;
}) {
  const intl = useIntl();
  const loaderRef = useRef<ZendeskWidgetLoader | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [loadState, setLoadState] = useState<ZendeskLoadState>("idle");
  // True only for intent-driven loads (facade click / footer trigger) — the
  // background idle load must not flip the facade into its spinner state.
  const [clickPending, setClickPending] = useState(false);
  // The real widget has painted its own entry point (launcher or open chat
  // window) — only then does the facade give up the corner. See the loader's
  // watchForWidgetPaint: the boot callback fires ~550ms before the launcher
  // iframe is actually visible.
  const [handedOff, setHandedOff] = useState(false);

  // Lazily create the loader on the client (never during SSR).
  const getLoader = (): ZendeskWidgetLoader | null => {
    if (!src) return null;
    loaderRef.current ??= createZendeskWidgetLoader({ src, nonce });
    return loaderRef.current;
  };

  const openChat = () => {
    const loader = getLoader();
    if (!loader) return;
    if (loader.getState() !== "ready") setClickPending(true);
    loader.load({ openOnReady: true });
  };

  useEffect(() => {
    if (!src) return;
    const loader = getLoader();
    if (!loader) return;

    // Once booted, wait for the real launcher / chat window to actually paint
    // before unmounting the facade (cancelable; re-arm-safe via ??=).
    let cancelPaintWatch: (() => void) | undefined;
    const armHandoff = () => {
      cancelPaintWatch ??= watchForWidgetPaint(() => {
        // Focus continuity: if a keyboard user is ON the facade when the real
        // launcher takes over (background load), hand focus to the launcher
        // iframe instead of dropping it to <body>. The click→open path needs
        // nothing here — the widget focuses its own chat window on open
        // (verified live: activeElement becomes iframe#webWidget).
        const facadeEl = buttonRef.current;
        if (facadeEl && document.activeElement === facadeEl) {
          const realEntry =
            document.getElementById(ZENDESK_LAUNCHER_IFRAME_ID) ??
            document.getElementById(ZENDESK_WIDGET_IFRAME_ID);
          realEntry?.focus();
        }
        setHandedOff(true);
      });
    };

    setLoadState(loader.getState());
    if (loader.getState() === "ready") armHandoff();
    const unsubscribe = loader.subscribe(state => {
      setLoadState(state);
      if (state === "ready") armHandoff();
      // A failed load re-arms the facade (retry on next click).
      if (state === "error") setClickPending(false);
    });

    // Footer "Live Chat" trigger (icon-links-grid) requests an open here.
    const handleOpenEvent = () => openChat();
    window.addEventListener(ZENDESK_OPEN_EVENT, handleOpenEvent);

    // Background load: first interaction arms an idle callback so the real
    // widget (and its console-side proactive behavior) resumes without a
    // click, but never competes with first paint / hydration and never loads
    // on an interactionless (Lighthouse) pass.
    let idleHandle: number | undefined;
    let idleScheduled = false;
    const removeInteractionListeners = () => {
      for (const event of INTERACTION_EVENTS) {
        window.removeEventListener(event, handleFirstInteraction);
      }
    };
    const handleFirstInteraction = () => {
      removeInteractionListeners();
      if (idleScheduled) return;
      idleScheduled = true;
      const idleLoad = () => loader.load();
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(idleLoad, { timeout: 3000 });
      } else {
        idleHandle = window.setTimeout(idleLoad, 1500);
      }
    };
    for (const event of INTERACTION_EVENTS) {
      window.addEventListener(event, handleFirstInteraction, {
        passive: true,
      });
    }

    return () => {
      unsubscribe();
      cancelPaintWatch?.();
      window.removeEventListener(ZENDESK_OPEN_EVENT, handleOpenEvent);
      removeInteractionListeners();
      if (idleHandle !== undefined) {
        if (typeof window.cancelIdleCallback === "function") {
          window.cancelIdleCallback(idleHandle);
        } else {
          window.clearTimeout(idleHandle);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- getLoader/openChat are stable per (src, nonce)
  }, [src, nonce]);

  // ET-629: `src` going null mid-session (client-side navigation into
  // `/intake/*`) only unmounts this facade — a real widget that already loaded
  // on an earlier page keeps its launcher in the corner. Hide it while
  // suppressed and restore it when `src` returns. `hiddenByUs` guards `show`
  // so we never un-hide a launcher the Zendesk account config hides itself.
  const hiddenByUs = useRef(false);
  useEffect(() => {
    if (src) {
      if (hiddenByUs.current) {
        hiddenByUs.current = false;
        showWidget();
      }
      return;
    }
    if (hideWidget()) hiddenByUs.current = true;
    // A load still in flight when we suppressed (zE not defined yet, or booting
    // after the hide) would reveal the launcher on boot — hide again then.
    const loader = loaderRef.current;
    if (loader?.getState() !== "loading") return;
    return loader.subscribe(state => {
      if (state === "ready" && hideWidget()) hiddenByUs.current = true;
    });
  }, [src]);

  // Suppressed context, or the real widget's launcher / chat window has
  // painted (its own launcher — with all account-side display rules — owns
  // the corner from here on). The component stays mounted so the
  // footer-trigger listener keeps working.
  if (!src || handedOff) return null;

  // Spinner from intent-click until the real widget paints (`ready` still
  // pre-paint) — never for the background idle load.
  const busy =
    clickPending && (loadState === "loading" || loadState === "ready");

  return (
    <button
      ref={buttonRef}
      type="button"
      data-testid="chat-facade-launcher-button"
      aria-label={intl.formatMessage(
        busy ? messages.loadingChat : messages.openChat,
      )}
      aria-busy={busy || undefined}
      // aria-disabled (NOT disabled): a disabled element drops keyboard focus
      // to <body> mid-load; the click guard below keeps it inert instead.
      aria-disabled={busy || undefined}
      onClick={() => {
        if (!busy) openChat();
      }}
      // The real launcher renders in its own iframe with the widget's
      // system-ui stack — inheriting the site brand font here would make the
      // handoff visibly reflow the label.
      style={{
        fontWeight: 700,
        fontVariationSettings: `"opsz" 28`,
        verticalAlign: "middle",
        letterSpacing: "1.1px",
        fontFamily:
          "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
      // Mirrors the classic launcher (values verified live on fsa-develop):
      // fixed 10px/20px from the corner, 46px pill with 22px x-padding and a
      // 15px/700 white label + 20px glyph on desktop, 48px icon-only circle
      // on mobile (the widget UA-sniffs; the `sm` breakpoint approximates).
      // No shadow (the real launcher has none), brand color from the Zendesk
      // account config (see --zendesk-launcher-bg in app.css). z-index matches
      // the widget's own 999998 layer. Mobile-first classes, NOT `max-sm:` —
      // this repo's `screens` config contains object entries (`sm-max` etc.),
      // which disables Tailwind's `max-*` variants entirely.
      // `zendesk-chat-facade` is a hook for app.css's sticky-ATC-bar rule,
      // which lifts #launcher AND this facade above the PDP sticky bar on
      // mobile — keep them in sync or the handoff jumps.
      className="zendesk-chat-facade fixed bottom-[14px] right-[20px] z-[999998] flex h-12 w-12 cursor-pointer items-center justify-center rounded-full bg-[var(--zendesk-launcher-bg,#785aff)] text-[15px] font-bold text-white transition-opacity hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--zendesk-launcher-bg,#785aff)] sm:h-[46px] sm:w-auto sm:justify-start sm:gap-[6px] sm:px-[22px]"
    >
      {busy ? (
        // 20px like the glyph it replaces, so the pill width doesn't shift.
        <span
          aria-hidden="true"
          className="h-5 w-5 animate-spin rounded-full border-2 border-white/40 border-t-white"
        />
      ) : (
        <ZendeskChatIcon />
      )}
      {/* Icon-only below `sm`, like the real mobile launcher; the button's
          aria-label keeps it accessible. */}
      <span className="hidden sm:inline">
        {intl.formatMessage(messages.chatLabel)}
      </span>
    </button>
  );
}

// Copy of the chat glyph inside Zendesk's own launcher iframe, so the facade
// → real launcher handoff doesn't visibly swap icons.
function ZendeskChatIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="1 -1 14 18"
      xmlSpace="preserve"
      aria-hidden="true"
      fill="white"
      height={"23px"}
      width={"23px"}
    >
      <path d="M1.3,16c-0.7,0-1.1-0.3-1.2-0.8c-0.3-0.8,0.5-1.3,0.8-1.5c0.6-0.4,0.9-0.7,1-1c0-0.2-0.1-0.4-0.3-0.7c0,0,0-0.1-0.1-0.1 C0.5,10.6,0,9,0,7.4C0,3.3,3.4,0,7.5,0C11.6,0,15,3.3,15,7.4s-3.4,7.4-7.5,7.4c-0.5,0-1-0.1-1.5-0.2C3.4,15.9,1.5,16,1.5,16 C1.4,16,1.4,16,1.3,16z M3.3,10.9c0.5,0.7,0.7,1.5,0.6,2.2c0,0.1-0.1,0.3-0.1,0.4c0.5-0.2,1-0.4,1.6-0.7c0.2-0.1,0.4-0.2,0.6-0.1 c0,0,0.1,0,0.1,0c0.4,0.1,0.9,0.2,1.4,0.2c3,0,5.5-2.4,5.5-5.4S10.5,2,7.5,2C4.5,2,2,4.4,2,7.4c0,1.2,0.4,2.4,1.2,3.3 C3.2,10.8,3.3,10.8,3.3,10.9z"></path>
    </svg>
  );
}
